/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { PhoneOutgoing, RefreshCw, Clock, Flame, Voicemail, PhoneMissed, CalendarClock, ExternalLink, Sparkles, FileDown, Printer, ClipboardList, Check } from "lucide-react";
import { OutboundImport } from "../components/telephony/OutboundImport";

const PRIORITY = {
  3: { zh: "最优先", en: "Top", cls: "bg-red-100 text-red-700 border-red-200" },
  2: { zh: "高", en: "High", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  1: { zh: "一般", en: "Normal", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const MGR_ROLES = ["admin", "ceo", "sales_vp", "sales_manager"];
const reasonIcon = (r) => (r.includes("指派") ? ClipboardList : r.includes("留言") ? Voicemail : r.includes("未接") ? PhoneMissed : r.includes("高意向") ? Flame : r.includes("跟进") ? CalendarClock : Sparkles);

export default function OutboundList() {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMgr = MGR_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/telephony/outbound-list"); setData(d); }
    catch { setData(null); }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const dial = (number, name) => { if (number) window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number, name } })); };

  const rows = (data?.items || []).map((it, i) => ({
    "#": i + 1,
    [zh ? "优先级" : "Priority"]: (PRIORITY[it.priority] || PRIORITY[1])[zh ? "zh" : "en"],
    [zh ? "客户" : "Customer"]: it.name || "",
    [zh ? "号码" : "Number"]: it.number,
    [zh ? "意向" : "Intent"]: it.intentTag || "",
    [zh ? "原因" : "Reasons"]: (it.reasons || []).join(" / "),
  }));

  const exportExcel = () => {
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, zh ? "外呼清单" : "Outbound");
    XLSX.writeFile(wb, `outbound_list_${data?.date || new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const printList = () => {
    if (!rows.length) return;
    const cols = Object.keys(rows[0]);
    const head = cols.map((c) => `<th>${c}</th>`).join("");
    const body = rows.map((r) => `<tr>${cols.map((c) => `<td>${String(r[c] ?? "")}</td>`).join("")}</tr>`).join("");
    const bh = data?.bestHour != null ? `${String(data.bestHour).padStart(2, "0")}:00` : "-";
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>${zh ? "今日外呼清单" : "Outbound List"} ${data?.date || ""}</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;padding:24px;color:#0f172a}
      h1{font-size:20px;margin:0 0 4px}p{color:#64748b;margin:0 0 16px;font-size:13px}
      table{border-collapse:collapse;width:100%;font-size:13px}
      th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left}
      th{background:#f8fafc}</style></head><body>
      <h1>${zh ? "今日智能外呼清单" : "Smart Outbound List"}</h1>
      <p>${data?.date || ""} · ${zh ? "共" : ""} ${rows.length} ${zh ? "位客户" : "customers"} · ${zh ? "黄金拨打时段" : "Best hour"} ${bh}</p>
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="space-y-5" data-testid="outbound-list-page">
      <div className="flex items-center gap-3 flex-wrap">
        <PhoneOutgoing className="h-6 w-6 text-amber-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{zh ? "今日智能外呼清单" : "Smart Outbound List"}</h1>
          <p className="text-sm text-slate-400 mt-0.5">{zh ? "按 AI 意向 + 未回拨 + 跟进到期自动排序，助你先打最该打的客户" : "Prioritized by AI intent, missed callbacks & due follow-ups"}</p>
        </div>
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-3">
          {data?.bestHour != null && (
            <span data-testid="outbound-best-hour" className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
              <Clock className="h-3.5 w-3.5" />{zh ? `你的黄金拨打时段 ${String(data.bestHour).padStart(2, "0")}:00` : `Best hour ${String(data.bestHour).padStart(2, "0")}:00`}
            </span>
          )}
          <button data-testid="outbound-export" onClick={exportExcel} disabled={!(data?.items || []).length} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm text-emerald-700 border border-emerald-200 rounded-lg px-3 py-1.5 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <FileDown className="h-4 w-4" />{zh ? "导出Excel" : "Excel"}
          </button>
          <button data-testid="outbound-print" onClick={printList} disabled={!(data?.items || []).length} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <Printer className="h-4 w-4" />{zh ? "打印" : "Print"}
          </button>
          <button data-testid="outbound-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{zh ? "刷新" : "Refresh"}
          </button>
        </div>
      </div>

      {isMgr && <OutboundImport api={api} zh={zh} onImported={load} />}

      <div className="flex-wrap md:flex-nowrap flex items-center gap-2 text-sm text-slate-500" data-testid="outbound-count">
        <span className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold">{data?.count ?? 0}</span>
        {zh ? "位客户待外呼" : "customers to call"}
        {data?.imported > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5" data-testid="outbound-imported-count"><ClipboardList className="h-3 w-3" />{zh ? `经理指派 ${data.imported}（当日有效）` : `${data.imported} assigned by manager`}</span>}
      </div>

      {(data?.items || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="outbound-empty">
          <PhoneOutgoing className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">{zh ? "今日暂无待外呼客户" : "No customers to call today"}</p>
          <p className="text-sm text-slate-400 mt-1">{zh ? "当有未回拨来电、AI 高意向通话或今日跟进到期的客户时，会自动出现在这里。" : "Missed callbacks, high-intent calls and due follow-ups will appear here automatically."}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {data.items.map((it, idx) => {
            const p = PRIORITY[it.priority] || PRIORITY[1];
            return (
              <div key={it.number} data-testid={`outbound-item-${idx}`} data-imported={it.imported ? "1" : "0"} className={`flex items-center gap-4 rounded-xl border bg-white p-4 hover:shadow-sm transition-shadow ${it.imported ? "border-violet-200 bg-violet-50/30" : "border-slate-200"}`}>
                <div className="w-6 text-center text-sm font-bold text-slate-300 shrink-0">{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900 truncate">{it.name || (zh ? "未命名客户" : "Unknown")}</p>
                    {it.imported && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-600 text-white" data-testid={`outbound-assigned-${idx}`}><ClipboardList className="h-3 w-3" />{zh ? "经理指派" : "Assigned"}{it.importedBy ? ` · ${it.importedBy}` : ""}</span>}
                    {it.imported && it.called && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`outbound-called-${idx}`}><Check className="h-3 w-3" />{zh ? "今日已拨" : "Called"}</span>}
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${p.cls}`}>{zh ? p.zh : p.en}</span>
                    {it.intentTag && <span className="text-[10px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800">{it.intentTag}</span>}
                  </div>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">{it.number}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-1.5">
                    {(it.reasons || []).map((r, i) => {
                      const RI = reasonIcon(r);
                      return <span key={i} className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5"><RI className="h-3 w-3" />{r}</span>;
                    })}
                  </div>
                </div>
                <div className="flex-wrap md:flex-nowrap flex items-center gap-2 shrink-0">
                  {it.leadId && (
                    <button data-testid={`outbound-open-${idx}`} onClick={() => navigate(`/crm/leads/${it.leadId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-700 border border-slate-200 rounded-lg px-2.5 py-2">
                      <ExternalLink className="h-3.5 w-3.5" />{zh ? "客户" : "Lead"}
                    </button>
                  )}
                  <button data-testid={`outbound-call-${idx}`} onClick={() => dial(it.number, it.name)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3.5 py-2">
                    <PhoneOutgoing className="h-4 w-4" />{zh ? "拨打" : "Call"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
